/**
 * dcent-pdp-dual-structure.js — DCENT S 듀얼 구조 비교 섹션의 모바일 캐러셀 (drop-04 R5).
 *
 * 디자인 SoT: drop-04-20260806/src/components/DualStructure.tsx.
 * PC(≥1024)는 순수 CSS 2열 그리드라 JS 가 하는 일이 없다. 모바일에서만 동작한다.
 *
 *   무한 가로 스와이프 — 패널 2장을 3세트로 이어 붙이고 가운데 세트에서 시작한다.
 *   경계(앞뒤 반 세트)를 넘으면 한 세트 폭만큼 순간 이동해 양방향으로 끝없이 순환한다.
 *   3.5초마다 다음 패널로 자동 진행하고, 손으로 만지면 4초 동안 자동 진행을 미룬다.
 *
 * 무한 순환이라 화살표 비활성이 없다(가이드 §5 무한 캐러셀 규칙 — 유한 캐러셀과 반대다).
 *
 * ⚠ 프로그램 스크롤(화살표·인디케이터·자동) 중에는 경계 순간이동을 보류한다.
 *   smooth 스크롤이 노멀라이즈에 끊겨 제자리로 돌아온 것처럼 보이는 버그를 막는 장치다.
 *   보류가 끝나면 한 번에 되감는다(세트 내용이 같아 눈에 보이지 않는다).
 *
 * 담기·옵션 상태는 여기서 다루지 않는다 — 그쪽은 dcent-pdp-buy.js 전담이다(drop-04 계약 §3).
 * 전역 상태 0 · 다중 호출 안전. 테마 편집기 재로드 대응.
 */

const PANEL_GAP = 12; // 패널 사이 간격(px) — CSS .dcent-dds__cmp gap 과 같은 값
const PANELS_PER_SET = 2; // 한 세트 = 좌(일반 카드형) + 우(DCENT S)
const AUTO_MS = 3500; // 자동 진행 주기
const PAUSE_MS = 4000; // 수동 조작 후 자동 진행 유예
const SUPPRESS_MS = 600; // 프로그램 스크롤 동안 경계 순간이동 보류

/** 모션 최소화 선호 시 자동 순환을 끄고 이동을 즉시 점프로 낮춘다. */
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function initDualStructure(sec) {
  const scroller = sec.querySelector("[data-dcent-dds-scroller]");
  const dots = sec.querySelectorAll("[data-dcent-dds-dot]");
  const prev = sec.querySelector("[data-dcent-dds-prev]");
  const next = sec.querySelector("[data-dcent-dds-next]");
  if (!scroller) return;

  // PC 그리드에서는 아무것도 하지 않는다(원본 cmpIsLg 와 같은 판정).
  const isLg = () => window.matchMedia("(min-width: 1024px)").matches;
  const step = () => scroller.clientWidth + PANEL_GAP;
  const setWidth = () => step() * PANELS_PER_SET;

  let active = 0;
  let paused = false;
  let resumeTimer = null;
  let suppressTimer = null;

  const paint = () => {
    dots.forEach((d, i) => d.classList.toggle("is-active", i === active));
  };

  /** 경계를 넘었으면 한 세트 폭만큼 순간 이동해 가운데 세트로 되돌린다. */
  const normalize = () => {
    const w = setWidth();
    if (w <= 0) return;
    if (scroller.scrollLeft < w * 0.5) scroller.scrollLeft += w;
    else if (scroller.scrollLeft >= w * 1.5) scroller.scrollLeft -= w;
  };

  const slide = (target) => {
    if (suppressTimer) window.clearTimeout(suppressTimer);
    suppressTimer = window.setTimeout(() => {
      suppressTimer = null;
      normalize();
    }, SUPPRESS_MS);
    scroller.scrollTo({
      left: target,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  const sync = () => {
    if (isLg()) return;
    const s = step();
    if (s <= 0) return;
    if (!suppressTimer) normalize();
    active =
      ((Math.round(scroller.scrollLeft / s) % PANELS_PER_SET) +
        PANELS_PER_SET) %
      PANELS_PER_SET;
    paint();
  };

  /** 손으로 만진 뒤 일정 시간 자동 진행을 미룬다 — 수동 조작이 자동보다 우선한다. */
  const pauseFor = (ms) => {
    paused = true;
    if (resumeTimer) window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(() => {
      paused = false;
    }, ms);
  };

  const go = (dir) => {
    if (isLg()) return;
    pauseFor(PAUSE_MS);
    const s = step();
    slide((Math.round(scroller.scrollLeft / s) + dir) * s);
  };

  /** 인디케이터 탭 — 현재 위치에서 가장 가까운 해당 패널 슬롯으로 간다. */
  const goTo = (i) => {
    if (isLg()) return;
    pauseFor(PAUSE_MS);
    const s = step();
    const slot =
      Math.round((scroller.scrollLeft / s - i) / PANELS_PER_SET) *
        PANELS_PER_SET +
      i;
    slide(slot * s);
  };

  scroller.addEventListener("scroll", sync);
  scroller.addEventListener("touchstart", () => {
    paused = true;
    if (resumeTimer) window.clearTimeout(resumeTimer);
  });
  scroller.addEventListener("touchend", () => pauseFor(PAUSE_MS));
  scroller.addEventListener("touchcancel", () => pauseFor(PAUSE_MS));
  if (prev) prev.addEventListener("click", () => go(-1));
  if (next) next.addEventListener("click", () => go(1));
  dots.forEach((d, i) => d.addEventListener("click", () => goTo(i)));

  // 시작 위치 = 가운데 세트의 첫 패널. 폭이 바뀌면 같은 패널로 다시 앵커한다.
  const anchor = () => {
    if (isLg()) return;
    scroller.scrollLeft = setWidth() + active * step();
  };
  anchor();
  paint();

  // 폭이 실제로 바뀐 경우에만 다시 앵커한다 — 모바일 브라우저는 주소창이 접혔다 펴질 때도
  // resize 를 쏘는데(높이만 변함), 거기에 반응하면 스와이프 도중 위치가 튄다.
  let lastWidth = scroller.clientWidth;
  window.addEventListener("resize", () => {
    if (scroller.clientWidth === lastWidth) return;
    lastWidth = scroller.clientWidth;
    anchor();
  });

  if (!prefersReducedMotion()) {
    const timer = window.setInterval(() => {
      // 테마 편집기가 섹션을 교체하면 옛 노드가 남는다 — 떨어져 나간 뒤에는 타이머를 끈다.
      if (!scroller.isConnected) {
        window.clearInterval(timer);
        return;
      }
      if (paused || isLg() || document.hidden) return;
      const s = step();
      slide((Math.round(scroller.scrollLeft / s) + 1) * s);
    }, AUTO_MS);
  }
}

export function initDualStructures(root = document) {
  root.querySelectorAll("[data-dcent-dds]").forEach((sec) => {
    if (sec.__dcentDds) return;
    sec.__dcentDds = true;
    initDualStructure(sec);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initDualStructures());
  // 테마 편집기가 섹션 DOM 을 AJAX 교체할 때 재초기화(라이브 스토어는 미발생)
  document.addEventListener("shopify:section:load", (e) =>
    initDualStructures(e.target),
  );
}
